import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { DateTime } from '@common/data-models/complex-types/date-time';
import { NewlyCreatedRecords } from '@common/data-models/newly-created-records/newly-created-records';
import { DataType } from '@common/enums/data-type';
import { AppService } from '@suite/app.service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export interface TreeLikeNode<T> {
    id: string;
    uniqueId?: string;
    label?: string;
    itemsCount?: number;
    object?: T;
    children: TreeLikeNode<T>[];
    nodeLastInGroup?: boolean;
    nodeFirstInGroup?: boolean;
    linkedRecordType?: number;
    isMetadata?: boolean;
    isNewItems?: boolean;
    cost?: Observable<number>;
    totalCost?: number;
    index?: number;
    isDisabled?: boolean;
}

export interface TreeLikeFlatNode<T> {
    id: string;
    level: number;
    uniqueId?: string;
    itemsCount?: number;
    hasChildren: boolean;
    label?: string;
    object?: T;
    nodeLastInGroup?: boolean;
    nodeFirstInGroup?: boolean;
    linkedRecordType?: number;
    isMetadata?: boolean;
    isNewItems?: boolean;
    cost?: Observable<number>;
    totalCost?: number;
    index?: number;
    isDisabled?: boolean;
}

export class SearchResults {
    objectType?: number = null;

    static appService: AppService = null;
    static groupByField: string = null;
    static sortByField: any = null;
    // we want this to be "desc" by default, for dates.
    static sortOrder: string = 'asc';
    // we want this to be "desc" by default, for dates.
    static groupSortOrder: string = 'asc';

    static notFoundImageCache: { [id: string]: boolean } = {};
    static newlyAddedItems: NewlyCreatedRecords = null;

    users: Array<StorytellingUserRecord> = [];

    byGuid: { [id: string]: StorytellingRecord } = {};
    newGuids: { [id: string]: boolean } = {};
    newItemsCount: number = 0;

    treeLikeData: TreeLikeNode<StorytellingRecord>[] = [];
    onDispose = new Subject<void>();

    cardItemHeights: number[] = [];
    condensedItemHeights: number[] = [];
    cardItemHeightsCustomFields: number[] = [];

    readonly treeControl: FlatTreeControl<TreeLikeFlatNode<StorytellingRecord>, string> = new FlatTreeControl<TreeLikeFlatNode<StorytellingRecord>, string>(
        this.getNodeLevel,
        this.getIsNodeExpandable,
        {
            trackBy: node => node.id,
        }
    );
    readonly treeDataSource: MatTreeFlatDataSource<TreeLikeNode<StorytellingRecord>, TreeLikeFlatNode<StorytellingRecord>>;

    readonly viewChange = new BehaviorSubject<{ start: number; end: number }>({
        start: 0,
        end: Number.MAX_VALUE,
    });

    constructor() {
        const treeFlattener = new MatTreeFlattener<TreeLikeNode<StorytellingRecord>, TreeLikeFlatNode<StorytellingRecord>, string>(
            this.nodeTransformer,
            this.getNodeLevel,
            this.getIsNodeExpandable,
            this.getNodeChildren
        );

        // Populates our flattened data into the tree control
        this.treeDataSource = new MatTreeFlatDataSource(this.treeControl, treeFlattener);
    }

    public addUser(user: StorytellingRecord) {
        this.users.push(user);
        this.byGuid[user.Guid] = user;
    }

    public remove(rec: StorytellingRecord) {
        if (this.byGuid[rec.Guid]) {
            delete this.byGuid[rec.Guid];
        }
    }

    public sortGroups(_sortBy: any, _sortOrder: string, _groupSortOrder: string) {
        throw 'sortGroups() not implemented';
    }

    public rebuildGroups(_groupBy: string, _sortBy: any, _sortOrder: string, _groupSortOrder: string) {
        throw 'rebuildGroups() not implemented';
    }

    public dispose() {
        this.onDispose.next();
        SearchResults.notFoundImageCache = {};

        this.byGuid = {};
        this.newGuids = {};
        this.newItemsCount = 0;
    }

    public getGroups(): Array<string> {
        throw 'getGroups() not implemented';
    }

    public getGroupCount(_key: string): number {
        throw 'getGroupCount() not implemented';
    }

    public getAllResults(): Array<StorytellingRecord> {
        throw 'getAllResults() not implemented';
    }

    protected buildTree(_sections: any): void {
        throw 'buildTree() not implemented';
    }

    public searchTimestamp: DateTime = null;

    public updateNewGuids(recGuids: Array<{ [id: string]: any }>, guidsToSkip?: { [id: string]: boolean }) {
        for (const rec of recGuids) {
            const timestamp = DataType.fromRaw(rec.CreatedDateTime, DataType.DATE) as DateTime;
            if (guidsToSkip && guidsToSkip[rec.guid]) {
                continue;
            }
            if (!this.byGuid[rec.guid] && this.searchTimestamp && this.searchTimestamp?.get()?.valueOf() <= timestamp?.get()?.valueOf()) {
                if (!this.newGuids[rec.guid]) {
                    this.newGuids[rec.guid] = true;
                    this.newItemsCount++;
                }
            }
        }
    }

    // Function that maps a nested node to a flat node
    nodeTransformer(node: TreeLikeNode<StorytellingRecord>, level: number): TreeLikeFlatNode<StorytellingRecord> {
        return {
            id: node.id,
            label: node.label,
            uniqueId: node.uniqueId,
            index: node.index,
            itemsCount: node.itemsCount,
            level,
            hasChildren: node?.children?.length > 0,
            object: node.object,
            nodeLastInGroup: node.nodeLastInGroup,
            nodeFirstInGroup: node.nodeFirstInGroup,
            linkedRecordType: node?.linkedRecordType,
            isMetadata: node?.isMetadata,
            isNewItems: node?.isNewItems,
            cost: node?.cost,
            isDisabled: node.isDisabled,
        };
    }

    // Function that gets a flat node's level
    getNodeLevel({ level }: TreeLikeFlatNode<StorytellingRecord>) {
        return level;
    }

    // Function that determines whether a flat node is expandable or not
    getIsNodeExpandable({ hasChildren }: TreeLikeFlatNode<StorytellingRecord>) {
        return hasChildren;
    }

    // Function that returns a nested node's list of children
    getNodeChildren({ children }: TreeLikeNode<StorytellingRecord>) {
        return children;
    }
}
